/**
 * What the mat shows, worked out without drawing anything: which markers rest
 * in which tray, which are on their way and can be moved, what every tray's
 * forecast says, which slots a move would complete, and what the middle of the
 * ring reads. The canvas draws this; nothing about the rules lives there.
 *
 * Every forecast comes from the engine's own `previewTray` and every placement
 * from `planPlacement`, so what the mat promises and what `apply` does cannot
 * drift apart.
 */
import {
  MANIFESTS,
  MARKERS_PER_TRAY,
  previewTray,
  trajanTileSatisfied,
  type ActionKind,
  type Color,
  type PlayerView,
  type TrajanTile,
  type TrayIndex,
} from '@trajan/engine';
import type { MarkerSpot } from '../table/layout';
import { TRAY_COUNT, type MarkerRow } from './geometry';
import { planPlacement } from './placement';

export interface MovingMarker {
  /** Its place in the colour order — the identity a swap moves around. */
  index: number;
  color: Color;
  /** The marker that decides the action: the last one dropped. */
  last: boolean;
  /** Lifted by a tap or the keyboard, waiting to be put down on another. */
  held: boolean;
  /** Being dragged: drawn under the pointer, its place in the tray left open. */
  dragging: boolean;
}

export interface TrayShow {
  action: ActionKind;
  /** Markers that stay where they are. */
  resting: readonly Color[];
  /** Which row the resting markers fill: the only one, or the upper while others arrive. */
  restingRow: MarkerRow;
  /** Markers being placed or arranged: the ones that can be moved. */
  moving: MovingMarker[];
  movingRow: MarkerRow;
  forecast: { target: ActionKind; moved: number; completes: boolean } | null;
  plus2: boolean;
  state: 'idle' | 'open' | 'source' | 'target' | 'dim';
  /** What an empty tray says: "picked up" for the tray a move empties. */
  empty: string;
}

export interface SlotShow {
  tile: TrajanTile | null;
  arch: boolean;
  tone: 'none' | 'completes' | 'short' | 'free' | 'placed';
  /** The tray the markers would land last in sits beside this slot. */
  target: boolean;
  /** At setup: whether the markers beside it already hold the tile's colours. */
  match: boolean | null;
}

export type CentreShow =
  | { kind: 'standing'; vp: number; archHome: boolean }
  | {
      kind: 'placing';
      action: ActionKind;
      moved: number;
      completes: boolean;
      slot: number;
      alsoSetUp: number[];
    }
  | { kind: 'arranging' }
  | { kind: 'tiles'; placed: number; of: number; holding: TrajanTile | null };

export interface MatShow {
  trays: TrayShow[];
  slots: SlotShow[];
  centre: CentreShow;
  /** The route a tray's markers take: the open tray's, or the placement's. */
  route: { source: number; steps: number } | null;
  /** Where each movable marker sits, for the hit layer. */
  markers: MarkerSpot[];
}

export interface MatInteraction {
  /** A tray picked up, and the order its markers are in. */
  placing?: { tray: TrayIndex; order: readonly Color[] } | null;
  /** The twelve markers being arranged at setup, two per tray. */
  arranging?: readonly (readonly Color[])[] | null;
  /** A marker lifted by a tap or the keyboard. */
  held?: number | null;
  /** A marker being dragged: drawn under the pointer, not in its tray. */
  dragging?: number | null;
  /** The tray the pointer or the focus is on, for its route. */
  hover?: number | null;
  /** Setup: the starting Trajan tiles put on slots so far, and the one in hand. */
  tiles?: {
    slots: readonly number[];
    placed: Readonly<Record<number, TrajanTile>>;
    holding: TrajanTile | null;
  } | null;
}

const SLOT_BESIDE = MANIFESTS.trajanSlotBesideTray as TrayIndex[];

export function matShow(player: PlayerView, m: MatInteraction = {}): MatShow {
  const arranging = m.arranging ?? null;
  const shown = arranging ? { ...player, trays: arranging.map((t) => [...t]) } : player;
  const previews = Array.from({ length: TRAY_COUNT }, (_, i) =>
    previewTray(shown, i as TrayIndex, SLOT_BESIDE),
  );
  const plan = m.placing
    ? planPlacement(player, m.placing.tray, [...m.placing.order], SLOT_BESIDE)
    : null;
  const open = plan ? plan.source : (m.hover ?? null);
  const openPreview = plan || open === null ? null : (previews[open] ?? null);
  const held = m.held ?? null;

  const markers: MarkerSpot[] = [];
  const trays: TrayShow[] = Array.from({ length: TRAY_COUNT }, (_, i) => {
    const action = MANIFESTS.trayActions[i] as ActionKind;
    let resting: readonly Color[] = player.trays[i] ?? [];
    let moving: MovingMarker[] = [];
    if (arranging) {
      resting = [];
      moving = (arranging[i] ?? []).map((color, k) => ({
        index: i * MARKERS_PER_TRAY + k,
        color,
        last: false,
        held: held === i * MARKERS_PER_TRAY + k,
        dragging: m.dragging === i * MARKERS_PER_TRAY + k,
      }));
    } else if (plan) {
      if (plan.source === i) resting = [];
      moving = plan.drops
        .filter((d) => d.tray === i)
        .map((d) => ({
          index: d.index,
          color: d.color,
          last: d.last,
          held: held === d.index,
          dragging: m.dragging === d.index,
        }));
    }
    const restingRow: MarkerRow = moving.length > 0 && resting.length > 0 ? 'upper' : 'only';
    const movingRow: MarkerRow = resting.length > 0 ? 'lower' : 'only';
    moving.forEach((mk, k) =>
      markers.push({ index: mk.index, tray: i, k, count: moving.length, row: movingRow }),
    );
    const preview = previews[i];
    const state: TrayShow['state'] =
      plan?.source === i
        ? 'source'
        : plan?.target === i
          ? 'target'
          : open === i
            ? 'open'
            : open !== null && !plan
              ? 'dim'
              : 'idle';
    return {
      action,
      resting,
      restingRow,
      moving,
      movingRow,
      forecast:
        plan || !preview
          ? null
          : {
              target: MANIFESTS.trayActions[preview.target] as ActionKind,
              moved: preview.moved,
              completes: preview.trajanTile?.satisfied ?? false,
            },
      plus2: player.plus2Spaces.includes(action),
      state,
      empty: plan?.source === i ? 'picked up' : 'empty',
    };
  });

  const targetSlot = plan ? SLOT_BESIDE[plan.target] : undefined;
  const slots: SlotShow[] = Array.from({ length: TRAY_COUNT }, (_, i) => {
    const tiles = m.tiles ?? null;
    if (tiles && tiles.slots.includes(i)) {
      const tile = tiles.placed[i] ?? null;
      return {
        tile,
        arch: false,
        tone: tile ? 'placed' : 'free',
        target: false,
        // The engine's own test, asked of the tray beside the slot as it stands.
        match: tile ? trajanTileSatisfied(tile, player.trays[i] ?? []) : null,
      };
    }
    const tone: SlotShow['tone'] = plan
      ? plan.satisfied.includes(i)
        ? 'completes'
        : 'none'
      : openPreview?.trajanTile?.slot === i
        ? openPreview.trajanTile.satisfied
          ? 'completes'
          : 'short'
        : 'none';
    return {
      tile: player.trajanSlots[i] ?? null,
      arch: player.arch === i,
      tone,
      target: targetSlot === i,
      match: null,
    };
  });

  const centre: CentreShow = m.tiles
    ? {
        kind: 'tiles',
        placed: Object.keys(m.tiles.placed).length,
        of: m.tiles.slots.length,
        holding: m.tiles.holding,
      }
    : arranging
      ? { kind: 'arranging' }
      : plan
        ? {
            kind: 'placing',
            action: MANIFESTS.trayActions[plan.target] as ActionKind,
            moved: plan.moved,
            completes: plan.completesTarget,
            slot: targetSlot ?? 0,
            alsoSetUp: plan.satisfied.filter((s) => s !== targetSlot),
          }
        : { kind: 'standing', vp: player.vp, archHome: player.arch === 'center' };

  const steps = plan ? plan.moved : (openPreview?.moved ?? 0);
  const source = plan ? plan.source : openPreview?.source;
  return {
    trays,
    slots,
    centre,
    route: !arranging && steps > 0 && source !== undefined ? { source, steps } : null,
    markers,
  };
}
